import { PropsWithChildren, ReactNode } from "react";

import {
  Box,
  Button,
  ButtonProps,
  PlayButton,
  Spacer,
  Tooltip,
  getColor,
  useAudio,
} from "@aircall/tractor";

import { useTrackAudioSkip } from "@/hooks/useTrackAudioSkip/useTrackAudioSkip";
import { useTrackPause } from "@/hooks/useTrackPause/useTrackPause";

import { PlaybackControlsProps } from "./PlaybackControls.decl";

// TODO: Use https://github.com/pd4d10/vite-plugin-svgr
import Forward from "@/assets/next-ten.svg?react";
import Backward from "@/assets/previous-ten.svg?react";
import { useTrackPlaying } from "@/hooks/useTrackPlaying/useTrackPlaying";
import { usePlayerShortcut } from "./usePlayerShortcut";

import { DELAY } from "@/constants/tooltip.constants";
import styled from "@xstyled/styled-components";
import { useTranslation } from "react-i18next";

const SPECIAL_CHARS = {
  SPACE: "␣",
  LEFT: "←",
  RIGHT: "→",
};

const KeyShortCut = styled.kbd`
  border: 1px solid ${getColor("graphic-contrast-light")};
  border-radius: 4px;
  padding: 4px;
  width: 16px;
  height: 20px;
  margin-left: 8px;
`;

function PlayButtonContent() {
  const { t } = useTranslation();
  const { isPlaying } = useAudio();

  if (isPlaying) {
    return (
      <Box>
        {t("pause")}
        <KeyShortCut>{SPECIAL_CHARS["SPACE"]}</KeyShortCut>
      </Box>
    );
  }

  return (
    <Box>
      {t("play")}
      <KeyShortCut>{SPECIAL_CHARS["SPACE"]}</KeyShortCut>
    </Box>
  );
}

function SkipButton({
  children,
  onClick,
  backward,
  tooltipContent,
  ...rest
}: PropsWithChildren<ButtonProps> & {
  backward?: boolean;
  tooltipContent: ReactNode;
}) {
  const { fastForward, rewind } = useAudio();

  const handleOnClick: ButtonProps["onClick"] = (event) => {
    if (backward) {
      rewind();
    } else {
      fastForward();
    }

    onClick?.(event);
  };

  return (
    <Tooltip mouseEnterDelay={DELAY} content={tooltipContent}>
      <Button
        variant="secondary"
        size="small"
        mode="ghost"
        onClick={handleOnClick}
        {...rest}
      >
        {children}
      </Button>
    </Tooltip>
  );
}

export function PlaybackControls({
  color,
  loading,
}: Readonly<PlaybackControlsProps>) {
  usePlayerShortcut();
  const { t } = useTranslation();
  const { isPlaying } = useAudio();

  const onClick = useTrackPlaying("play_button");
  const { onPause } = useTrackPause();

  const { onClick: onClickAudioSkip } = useTrackAudioSkip();

  const handleOnClick = () => {
    if (!isPlaying) {
      onClick();
    } else {
      onPause();
    }
  };

  return (
    <Spacer
      alignItems="center"
      justifyContent="center"
      space="xxs"
      data-test="playback-controls"
    >
      <Tooltip mouseEnterDelay={DELAY} content={<PlayButtonContent />}>
        <Box onClick={handleOnClick} data-test="play-button-container">
          <PlayButton color={color} loading={loading} />
        </Box>
      </Tooltip>
      <SkipButton
        onClick={onClickAudioSkip("audio_skip_backward")}
        data-test="control-backward"
        backward
        tooltipContent={
          <Box>
            {t("rewind_10s")} <KeyShortCut>{SPECIAL_CHARS["LEFT"]}</KeyShortCut>
          </Box>
        }
      >
        <Backward />
      </SkipButton>
      <SkipButton
        onClick={onClickAudioSkip("audio_skip_forward")}
        data-test="control-forward"
        tooltipContent={
          <Box>
            {t("advance_10s")}
            <KeyShortCut>{SPECIAL_CHARS["RIGHT"]}</KeyShortCut>
          </Box>
        }
      >
        <Forward />
      </SkipButton>
    </Spacer>
  );
}
